import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Cell } from 'recharts';
const CapexTrackingDashboard: React.FC = () => {
  const kpis = [{
    title: 'Total Capex',
    value: '₹1,620 Cr',
    change: '+8.2%',
    isPositive: true,
    subtitle: 'Approved for FY24'
  }, {
    title: 'Approved vs Utilized %',
    value: '87.3%',
    change: '+5.1%',
    isPositive: true,
    subtitle: 'Utilization rate'
  }, {
    title: 'ROI %',
    value: '18.2%',
    change: '+1.5%',
    isPositive: true,
    subtitle: 'Average project ROI'
  }];
  // Capex vs Budget Data
  const capexBudgetData = [{
    project: 'LED Plant Expansion',
    budget: 450,
    actual: 428,
    variance: -22,
    color: '#10b981'
  }, {
    project: 'EV Battery Line',
    budget: 850,
    actual: 612,
    variance: -238,
    color: '#3b82f6'
  }, {
    project: 'Automation Upgrade',
    budget: 320,
    actual: 285,
    variance: -35,
    color: '#10b981'
  }, {
    project: 'R&D Center',
    budget: 280,
    actual: 295,
    variance: 15,
    color: '#ef4444'
  }, {
    project: 'Warehouse Expansion',
    budget: 185,
    actual: 178,
    variance: -7,
    color: '#10b981'
  }, {
    project: 'IT Infrastructure',
    budget: 145,
    actual: 142,
    variance: -3,
    color: '#10b981'
  }, {
    project: 'Quality Lab Setup',
    budget: 125,
    actual: 118,
    variance: -7,
    color: '#10b981'
  }, {
    project: 'Solar Power Plant',
    budget: 265,
    actual: 252,
    variance: -13,
    color: '#10b981'
  }];
  // ROI Trend Data (Quarterly)
  const roiTrendData = [{
    quarter: 'Q1 FY23',
    actualROI: 14.5,
    projectedROI: 15.2,
    benchmark: 16.0
  }, {
    quarter: 'Q2 FY23',
    actualROI: 15.2,
    projectedROI: 15.8,
    benchmark: 16.0
  }, {
    quarter: 'Q3 FY23',
    actualROI: 15.8,
    projectedROI: 16.2,
    benchmark: 16.0
  }, {
    quarter: 'Q4 FY23',
    actualROI: 16.4,
    projectedROI: 16.8,
    benchmark: 16.0
  }, {
    quarter: 'Q1 FY24',
    actualROI: 16.8,
    projectedROI: 17.2,
    benchmark: 16.5
  }, {
    quarter: 'Q2 FY24',
    actualROI: 17.4,
    projectedROI: 17.8,
    benchmark: 16.5
  }, {
    quarter: 'Q3 FY24',
    actualROI: 17.9,
    projectedROI: 18.2,
    benchmark: 16.5
  }, {
    quarter: 'Q4 FY24',
    actualROI: 18.2,
    projectedROI: 18.5,
    benchmark: 16.5
  }];
  // Project Delays Heatmap Data
  const projectDelayData = [{
    project: 'LED Plant Expansion',
    planning: 'On Time',
    execution: 'On Time',
    commissioning: 'On Time',
    overallStatus: 'completed',
    delayDays: 0
  }, {
    project: 'EV Battery Line',
    planning: 'Delayed',
    execution: 'On Time',
    commissioning: 'In Progress',
    overallStatus: 'delayed',
    delayDays: 45
  }, {
    project: 'Automation Upgrade',
    planning: 'On Time',
    execution: 'On Time',
    commissioning: 'In Progress',
    overallStatus: 'on-track',
    delayDays: 0
  }, {
    project: 'R&D Center',
    planning: 'On Time',
    execution: 'Delayed',
    commissioning: 'Delayed',
    overallStatus: 'delayed',
    delayDays: 28
  }, {
    project: 'Warehouse Expansion',
    planning: 'On Time',
    execution: 'On Time',
    commissioning: 'On Time',
    overallStatus: 'completed',
    delayDays: 0
  }, {
    project: 'IT Infrastructure',
    planning: 'On Time',
    execution: 'On Time',
    commissioning: 'On Time',
    overallStatus: 'on-track',
    delayDays: 0
  }, {
    project: 'Quality Lab Setup',
    planning: 'On Time',
    execution: 'Delayed',
    commissioning: 'Not Started',
    overallStatus: 'at-risk',
    delayDays: 15
  }, {
    project: 'Solar Power Plant',
    planning: 'On Time',
    execution: 'On Time',
    commissioning: 'In Progress',
    overallStatus: 'on-track',
    delayDays: 0
  }];
  // Capex Project List Table
  const capexProjectData = [{
    project: 'LED Plant Expansion',
    category: 'Capacity',
    budget: 450,
    spent: 428,
    utilization: 95.1,
    startDate: 'Jan 2024',
    completionDate: 'Oct 2024',
    status: 'Completed',
    expectedROI: 18.5,
    actualROI: 18.8,
    paybackPeriod: 4.2,
    delayDays: 0
  }, {
    project: 'EV Battery Line',
    category: 'New Product',
    budget: 850,
    spent: 612,
    utilization: 72.0,
    startDate: 'Mar 2024',
    completionDate: 'Mar 2025',
    status: 'In Progress',
    expectedROI: 22.0,
    actualROI: null,
    paybackPeriod: 3.8,
    delayDays: 45
  }, {
    project: 'Automation Upgrade',
    category: 'Efficiency',
    budget: 320,
    spent: 285,
    utilization: 89.1,
    startDate: 'Feb 2024',
    completionDate: 'Dec 2024',
    status: 'In Progress',
    expectedROI: 16.2,
    actualROI: null,
    paybackPeriod: 5.1,
    delayDays: 0
  }, {
    project: 'R&D Center',
    category: 'Innovation',
    budget: 280,
    spent: 295,
    utilization: 105.4,
    startDate: 'Jan 2024',
    completionDate: 'Nov 2024',
    status: 'At Risk',
    expectedROI: 14.8,
    actualROI: null,
    paybackPeriod: 6.2,
    delayDays: 28
  }, {
    project: 'Warehouse Expansion',
    category: 'Infrastructure',
    budget: 185,
    spent: 178,
    utilization: 96.2,
    startDate: 'Apr 2024',
    completionDate: 'Sep 2024',
    status: 'Completed',
    expectedROI: 15.5,
    actualROI: 15.8,
    paybackPeriod: 5.8,
    delayDays: 0
  }, {
    project: 'IT Infrastructure',
    category: 'Technology',
    budget: 145,
    spent: 142,
    utilization: 97.9,
    startDate: 'May 2024',
    completionDate: 'Jan 2025',
    status: 'On Track',
    expectedROI: 19.2,
    actualROI: null,
    paybackPeriod: 4.5,
    delayDays: 0
  }, {
    project: 'Quality Lab Setup',
    category: 'Quality',
    budget: 125,
    spent: 118,
    utilization: 94.4,
    startDate: 'Jun 2024',
    completionDate: 'Feb 2025',
    status: 'At Risk',
    expectedROI: 17.8,
    actualROI: null,
    paybackPeriod: 4.8,
    delayDays: 15
  }, {
    project: 'Solar Power Plant',
    category: 'Sustainability',
    budget: 265,
    spent: 252,
    utilization: 95.1,
    startDate: 'Mar 2024',
    completionDate: 'Dec 2024',
    status: 'On Track',
    expectedROI: 21.5,
    actualROI: null,
    paybackPeriod: 4.0,
    delayDays: 0
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-700';
      case 'On Track':
        return 'bg-blue-100 text-blue-700';
      case 'In Progress':
        return 'bg-yellow-100 text-yellow-700';
      case 'At Risk':
        return 'bg-orange-100 text-orange-700';
      case 'Delayed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getPhaseColor = (phase: string) => {
    switch (phase) {
      case 'On Time':
        return 'bg-green-500';
      case 'Delayed':
        return 'bg-red-500';
      case 'In Progress':
        return 'bg-blue-500';
      case 'Not Started':
        return 'bg-gray-300';
      default:
        return 'bg-gray-300';
    }
  };
  return <div data-id="element-3663">
      <DescriptionPanel title="Capex Tracking & ROI Analysis" description="Comprehensive capital expenditure tracking across all projects. Monitor budget utilization, ROI performance, project delays, and completion status to ensure optimal capital allocation and returns on investment." insights={['Total capex of ₹1,620 Cr approved for FY24 with 87.3% utilization', 'Average project ROI at 18.2%, exceeding benchmark of 16.5%', 'LED Plant Expansion completed on time with 18.8% ROI', 'EV Battery Line showing 45-day delay but strong projected ROI of 22%']} data-id="element-3664" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-3665">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-3666">
          Capex Performance Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3667">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-3668">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-3669">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-3670">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-3671">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-3672">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-3673" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-3674" />}
                {kpi.change} vs last year
              </div>
            </Card>)}
        </div>
      </div>
      {/* Capex vs Budget and ROI Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-3675">
        {/* Capex vs Budget Chart */}
        <Card className="p-6" data-id="element-3676">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-3677">
            Capex vs Budget Comparison
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-3678">
            Project-wise budget utilization (₹ Cr)
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-3679">
            <BarChart data={capexBudgetData} layout="vertical" data-id="element-3680">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-3681" />
              <XAxis type="number" tick={{
              fontSize: 12
            }} data-id="element-3682" />
              <YAxis dataKey="project" type="category" width={120} tick={{
              fontSize: 10
            }} data-id="element-3683" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `₹${value} Cr`} data-id="element-3684" />
              <Legend data-id="element-3685" />
              <Bar dataKey="budget" fill="#94a3b8" name="Budget" data-id="element-3686" />
              <Bar dataKey="actual" name="Actual Spend" data-id="element-3687">
                {capexBudgetData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-3688" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-3689">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-3690">
              <div className="text-center" data-id="element-3691">
                <div className="text-gray-600" data-id="element-3692">Total Budget</div>
                <div className="text-lg font-bold text-gray-900" data-id="element-3693">₹2,620 Cr</div>
              </div>
              <div className="text-center" data-id="element-3694">
                <div className="text-gray-600" data-id="element-3695">Total Spent</div>
                <div className="text-lg font-bold text-blue-600" data-id="element-3696">₹2,310 Cr</div>
              </div>
              <div className="text-center" data-id="element-3697">
                <div className="text-gray-600" data-id="element-3698">Variance</div>
                <div className="text-lg font-bold text-green-600" data-id="element-3699">-₹310 Cr</div>
              </div>
            </div>
          </div>
        </Card>
        {/* ROI Trend Chart */}
        <Card className="p-6" data-id="element-3700">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-3701">
            ROI Trend Analysis
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-3702">
            Quarterly ROI performance (%)
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-3703">
            <LineChart data={roiTrendData} data-id="element-3704">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-3705" />
              <XAxis dataKey="quarter" tick={{
              fontSize: 11
            }} data-id="element-3706" />
              <YAxis tick={{
              fontSize: 12
            }} domain={[12, 20]} data-id="element-3707" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `${value.toFixed(1)}%`} data-id="element-3708" />
              <Legend data-id="element-3709" />
              <Line type="monotone" dataKey="actualROI" stroke="#10b981" strokeWidth={3} name="Actual ROI" dot={{
              r: 5
            }} data-id="element-3710" />
              <Line type="monotone" dataKey="projectedROI" stroke="#3b82f6" strokeWidth={2} strokeDasharray="5 5" name="Projected ROI" dot={{
              r: 4
            }} data-id="element-3711" />
              <Line type="monotone" dataKey="benchmark" stroke="#ef4444" strokeWidth={2} strokeDasharray="3 3" name="Industry Benchmark" dot={{
              r: 3
            }} data-id="element-3712" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-3713">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-3714">
              <div className="text-center" data-id="element-3715">
                <div className="text-gray-600" data-id="element-3716">Current ROI</div>
                <div className="text-xl font-bold text-green-600" data-id="element-3717">18.2%</div>
              </div>
              <div className="text-center" data-id="element-3718">
                <div className="text-gray-600" data-id="element-3719">Projected ROI</div>
                <div className="text-xl font-bold text-blue-600" data-id="element-3720">18.5%</div>
              </div>
              <div className="text-center" data-id="element-3721">
                <div className="text-gray-600" data-id="element-3722">vs Benchmark</div>
                <div className="text-xl font-bold text-green-600" data-id="element-3723">+1.7%</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Project Delays Heatmap */}
      <Card className="p-6 mb-8" data-id="element-3724">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-3725">
          Project Delays Heatmap
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-3726">
          Phase-wise status tracking across all projects
        </p>
        <div className="overflow-x-auto" data-id="element-3727">
          <table className="w-full" data-id="element-3728">
            <thead data-id="element-3729">
              <tr className="border-b-2 border-gray-300" data-id="element-3730">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3731">
                  Project
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3732">
                  Planning
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3733">
                  Execution
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3734">
                  Commissioning
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3735">
                  Overall Status
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3736">
                  Delay (Days)
                </th>
              </tr>
            </thead>
            <tbody data-id="element-3737">
              {projectDelayData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-3738">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-3739">
                    {row.project}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-3740">
                    <div className={`inline-block w-20 h-8 rounded ${getPhaseColor(row.planning)} flex items-center justify-center`} data-id="element-3741">
                      <span className="text-xs font-semibold text-white" data-id="element-3742">
                        {row.planning}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-3743">
                    <div className={`inline-block w-20 h-8 rounded ${getPhaseColor(row.execution)} flex items-center justify-center`} data-id="element-3744">
                      <span className="text-xs font-semibold text-white" data-id="element-3745">
                        {row.execution}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-3746">
                    <div className={`inline-block w-20 h-8 rounded ${getPhaseColor(row.commissioning)} flex items-center justify-center`} data-id="element-3747">
                      <span className="text-xs font-semibold text-white" data-id="element-3748">
                        {row.commissioning}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-3749">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(row.overallStatus === 'completed' ? 'Completed' : row.overallStatus === 'on-track' ? 'On Track' : row.overallStatus === 'at-risk' ? 'At Risk' : 'Delayed')}`} data-id="element-3750">
                      {row.overallStatus === 'completed' ? 'Completed' : row.overallStatus === 'on-track' ? 'On Track' : row.overallStatus === 'at-risk' ? 'At Risk' : 'Delayed'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-3751">
                    <span className={`text-sm font-bold ${row.delayDays === 0 ? 'text-green-600' : row.delayDays <= 20 ? 'text-orange-600' : 'text-red-600'}`} data-id="element-3752">
                      {row.delayDays === 0 ? 'On Time' : `+${row.delayDays}d`}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-3753">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-3754">
            <div className="text-center" data-id="element-3755">
              <div className="text-gray-600" data-id="element-3756">Total Projects</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-3757">8</div>
            </div>
            <div className="text-center" data-id="element-3758">
              <div className="text-gray-600" data-id="element-3759">Completed</div>
              <div className="text-xl font-bold text-green-600" data-id="element-3760">2</div>
            </div>
            <div className="text-center" data-id="element-3761">
              <div className="text-gray-600" data-id="element-3762">On Track</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-3763">3</div>
            </div>
            <div className="text-center" data-id="element-3764">
              <div className="text-gray-600" data-id="element-3765">At Risk</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-3766">2</div>
            </div>
            <div className="text-center" data-id="element-3767">
              <div className="text-gray-600" data-id="element-3768">Delayed</div>
              <div className="text-xl font-bold text-red-600" data-id="element-3769">1</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Capex Project List Table */}
      <Card className="p-6" data-id="element-3770">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-3771">
          Detailed Capex Project List - FY24
        </h3>
        <div className="overflow-x-auto" data-id="element-3772">
          <table className="w-full" data-id="element-3773">
            <thead data-id="element-3774">
              <tr className="border-b-2 border-gray-300" data-id="element-3775">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3776">
                  Project
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3777">
                  Category
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3778">
                  Budget
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3779">
                  Spent
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3780">
                  Utilization %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3781">
                  Start Date
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3782">
                  Completion
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3783">
                  Status
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3784">
                  Expected ROI
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3785">
                  Actual ROI
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3786">
                  Payback (Yrs)
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3787">
                  Delay
                </th>
              </tr>
            </thead>
            <tbody data-id="element-3788">
              {capexProjectData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-3789">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-3790">
                    {row.project}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-3791">
                    <span className="inline-flex px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700" data-id="element-3792">
                      {row.category}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-3793">
                    ₹{row.budget} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-blue-600" data-id="element-3794">
                    ₹{row.spent} Cr
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-3795">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.utilization <= 100 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} data-id="element-3796">
                      {row.utilization.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-center text-gray-700" data-id="element-3797">
                    {row.startDate}
                  </td>
                  <td className="py-3 px-4 text-sm text-center text-gray-700" data-id="element-3798">
                    {row.completionDate}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-3799">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(row.status)}`} data-id="element-3800">
                      {row.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-3801">
                    {row.expectedROI.toFixed(1)}%
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-green-600" data-id="element-3802">
                    {row.actualROI ? `${row.actualROI.toFixed(1)}%` : '-'}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-3803">
                    {row.paybackPeriod.toFixed(1)} yrs
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-3804">
                    <span className={`text-xs font-bold ${row.delayDays === 0 ? 'text-green-600' : row.delayDays <= 20 ? 'text-orange-600' : 'text-red-600'}`} data-id="element-3805">
                      {row.delayDays === 0 ? 'On Time' : `+${row.delayDays}d`}
                    </span>
                  </td>
                </tr>)}
              {/* Total Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold" data-id="element-3806">
                <td className="py-3 px-4 text-sm text-gray-900" data-id="element-3807">TOTAL</td>
                <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-3808">
                  -
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-3809">
                  ₹2,620 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-blue-600" data-id="element-3810">
                  ₹2,310 Cr
                </td>
                <td className="py-3 px-4 text-center" data-id="element-3811">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700" data-id="element-3812">
                    88.2%
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-center text-gray-700" data-id="element-3813">
                  -
                </td>
                <td className="py-3 px-4 text-sm text-center text-gray-700" data-id="element-3814">
                  -
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-3815">
                  -
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-3816">
                  18.2%
                </td>
                <td className="py-3 px-4 text-sm text-right text-green-600" data-id="element-3817">
                  17.3%
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-3818">
                  4.8 yrs
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-3819">
                  -
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-3820">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-3821">
            <div className="text-center" data-id="element-3822">
              <div className="text-gray-600" data-id="element-3823">Total Budget</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-3824">₹2,620 Cr</div>
            </div>
            <div className="text-center" data-id="element-3825">
              <div className="text-gray-600" data-id="element-3826">Total Spent</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-3827">₹2,310 Cr</div>
            </div>
            <div className="text-center" data-id="element-3828">
              <div className="text-gray-600" data-id="element-3829">Avg ROI</div>
              <div className="text-xl font-bold text-green-600" data-id="element-3830">18.2%</div>
            </div>
            <div className="text-center" data-id="element-3831">
              <div className="text-gray-600" data-id="element-3832">Avg Payback</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-3833">4.8 yrs</div>
            </div>
            <div className="text-center" data-id="element-3834">
              <div className="text-gray-600" data-id="element-3835">Projects On Time</div>
              <div className="text-xl font-bold text-green-600" data-id="element-3836">5/8</div>
            </div>
            <div className="text-center" data-id="element-3837">
              <div className="text-gray-600" data-id="element-3838">Best ROI Project</div>
              <div className="text-xl font-bold text-green-600" data-id="element-3839">EV Battery</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default CapexTrackingDashboard;